import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SectionHeading } from "../components/shared/SectionHeading";
import { siteContent, images } from "../data/content";

export const Products = () => {
  const specs = siteContent.product.specs;
  const specRows = [
    { label: "Material", value: specs.material },
    { label: "Shape", value: specs.shape },
    { label: "Size", value: specs.size },
    { label: "Application", value: specs.application },
    { label: "Ash Content", value: specs.ashContent },
    { label: "Moisture", value: specs.moisture },
    { label: "Fixed Carbon", value: specs.fixedCarbon },
    { label: "Burn Time", value: specs.burnTime },
    { label: "Calorific Value", value: specs.calorificValue },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal-paper border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
              Product Catalog
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              data-testid="products-title"
            >
              Our Products
            </h1>
            <p className="text-lg text-zinc-400">
              Premium coconut shell charcoal briquettes designed for consistent performance in shisha/hookah applications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Card */}
      <section className="py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 aspect-video bg-charcoal-subtle border border-white/5 overflow-hidden">
                  <img
                    src={images.charcoalGlow}
                    alt="Cube 25mm Charcoal Briquette"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-charcoal-subtle border border-white/5 overflow-hidden">
                  <img
                    src={images.productCube}
                    alt="Charcoal cubes close-up"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-charcoal-subtle border border-white/5 overflow-hidden">
                  <img
                    src={images.hookah}
                    alt="Hookah application"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block bg-coconut text-charcoal font-mono text-sm px-3 py-1 mb-4">
                MAIN PRODUCT
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-4">
                {siteContent.product.name}
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Our flagship product - precision-cut 25mm cubes made from 100% coconut shell. 
                Designed for optimal heat distribution and long-lasting performance in premium hookah setups.
              </p>

              {/* Specs Table */}
              <div className="bg-charcoal-paper border border-white/5 mb-8" data-testid="product-specs-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5">
                      <TableHead className="font-heading uppercase text-white">Specification</TableHead>
                      <TableHead className="font-heading uppercase text-white">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {specRows.map((row, index) => (
                      <TableRow key={index} className="border-white/5">
                        <TableCell className="text-zinc-400">{row.label}</TableCell>
                        <TableCell className="text-white font-medium">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
                  data-testid="product-quote-btn"
                >
                  <Link to="/contact">
                    Get a Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 font-heading uppercase"
                  data-testid="product-wa-btn"
                >
                  <a
                    href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="mr-2 w-4 h-4" />
                    WhatsApp for Fast Response
                  </a>
                </Button>
              </div>

              {/* PDF Download */}
              <div className="mt-6">
                <Button
                  asChild
                  variant="ghost"
                  className="text-zinc-300 hover:text-white"
                  data-testid="download-pdf-btn"
                >
                  <a
                    href={siteContent.product.specPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="mr-2 w-4 h-4" />
                    Download Product Specs (PDF)
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Packaging Options */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            title="Packaging Options"
            subtitle="Flexible packaging configurations to meet your market requirements"
          />

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-charcoal border border-white/5 p-8"
            >
              <h4 className="font-heading text-white uppercase text-lg mb-4">Inner Box</h4>
              <ul className="space-y-2 text-zinc-400">
                {siteContent.product.packaging.innerBox.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-coconut" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-charcoal border border-white/5 p-8"
            >
              <h4 className="font-heading text-white uppercase text-lg mb-4">Master Carton</h4>
              <ul className="space-y-2 text-zinc-400">
                {siteContent.product.packaging.masterCarton.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-coconut" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-charcoal border border-white/5 p-8"
            >
              <h4 className="font-heading text-white uppercase text-lg mb-4">Private Label</h4>
              <p className="text-zinc-400 mb-4">
                Full customization with your branding, logos, and language options.
              </p>
              <Link
                to="/packaging"
                className="text-coconut hover:text-coconut-hover font-medium inline-flex items-center"
              >
                Learn More <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
            Interested in Our Product?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            Contact us for pricing, samples, or to discuss your specific requirements. 
            We're ready to support your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
            >
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 font-heading uppercase"
            >
              <a
                href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
